/**
 * CollectorUp analytics · shared module (Phase 1 + Phase 2).
 *
 * Providers:
 *   · Google Analytics 4    — REACT_APP_GA4_MEASUREMENT_ID   (e.g. "G-XXXXXXXXXX")
 *   · Microsoft Clarity     — REACT_APP_CLARITY_PROJECT_ID   (e.g. "abcd1234")
 *
 * Environment gate:
 *   · REACT_APP_ANALYTICS_ENABLED = "true"|"false"|""
 *     Preview / local default is DISABLED so test activity never
 *     pollutes production reports. Production toggles it on with an
 *     explicit "true" in the deployed .env.
 *
 * Consent gate (deny by default per project spec §7):
 *   · Neither GA4 nor Clarity load until `setAnalyticsConsent("granted")`
 *     is called via the consent banner. Rejecting the banner locks
 *     both providers off for the visitor.
 *   · The consent choice is stored in `localStorage` under
 *     ANALYTICS_CONSENT_KEY; visitors can change it later via the
 *     "Analytics Preferences" link.
 *   · Google Consent Mode v2 signals are emitted alongside so GA4
 *     itself honours the deny state even if the tag is loaded via
 *     GTM in a later phase.
 *
 * Sensitive-URL sanitizer:
 *   · Any URL fragment that matches a dynamic-token pattern (order
 *     public_id, magic token, quote id, claim code, admin identifier
 *     etc.) is replaced with a category label BEFORE the URL is
 *     handed to GA4 or Clarity. Sensitive query params are stripped.
 *     Preserved: five standard UTM params + `page`/`tab` selectors.
 *
 * Admin traffic:
 *   · When the current URL is under `/admin`, page views carry
 *     `user_type: "admin"` so GA4 can filter Gil's activity out of
 *     customer traffic without a separate property.
 *
 * Idempotency:
 *   · Major conversion events (missing_quote_generated,
 *     missing_order_submitted, certificate_order_submitted,
 *     signup_completed, quote_converted_to_order,
 *     certificate_claim_completed) are gated by an in-memory
 *     per-key guard so a stray double-click NEVER fires the event
 *     twice inside one page life.
 *
 * The module exports a tiny, boring API — every call is a safe no-op
 * when the provider is disabled or consent is denied, and NEVER
 * throws. The site keeps working without any IDs configured.
 */

const RAW_GA_ID  = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
const RAW_CL_ID  = (process.env.REACT_APP_CLARITY_PROJECT_ID || "").trim();
const ANALYTICS_ENABLED_ENV =
  (process.env.REACT_APP_ANALYTICS_ENABLED || "").trim().toLowerCase() === "true";

// Provider validity — matches the actual ID formats.
const GA_ID_VALID  = /^G-[A-Z0-9]{6,}$/i.test(RAW_GA_ID);
const CL_ID_VALID  = RAW_CL_ID.length >= 4;

// Master gate: env must be flipped AND at least one provider must
// have a valid ID. Preview defaults to disabled.
const GA_ELIGIBLE  = ANALYTICS_ENABLED_ENV && GA_ID_VALID;
const CL_ELIGIBLE  = ANALYTICS_ENABLED_ENV && CL_ID_VALID;

/**
 * `public/index.html` hardcodes the standard GA4 snippet on the
 * production hostname. When that snippet has run, `window.gtag` is
 * live and `data-analytics-source="index-html"` is queryable — even
 * though `REACT_APP_GA4_MEASUREMENT_ID` may be empty in the build
 * env. This helper lets the runtime detect the pre-installed tag so
 * page-view dispatch works with or without the env vars.
 */
function _hasPreinstalledGA4() {
  if (typeof document === "undefined") return false;
  return !!document.querySelector('script[data-analytics-source="index-html"]');
}

/**
 * Returns true when GA4 is EITHER env-configured (dynamic loader) OR
 * pre-installed by the root `index.html` snippet. Consent gating is
 * enforced separately by callers.
 */
function _ga4Available() {
  if (GA_ELIGIBLE) return true;
  return _hasPreinstalledGA4();
}

/**
 * P-Admin exclusion helper — CollectorUp requires GA4 to NEVER
 * track any admin surface regardless of device / network / IP.
 * Any URL whose pathname starts with `/admin` (case-insensitive)
 * is treated as fully excluded:
 *   · `initAnalytics()` does not boot providers on direct admin entry
 *   · `trackPageView()` early-returns before touching any state
 *   · `track()` early-returns for the current admin path
 * This is intentionally case-insensitive so `/ADMIN`, `/Admin/...`
 * etc. behave identically to `/admin/...`.
 */
function _isAdminPath(rawPath) {
  if (typeof rawPath !== "string" || !rawPath) return false;
  const p = rawPath.split("?")[0].toLowerCase();
  return p === "/admin" || p.startsWith("/admin/");
}

function _currentPathLower() {
  if (typeof window === "undefined" || !window.location) return "";
  return (window.location.pathname || "").toLowerCase();
}

export const ANALYTICS_CONSENT_KEY = "cu.analytics.consent.v1";
const CONSENT_GRANTED = "granted";
const CONSENT_DENIED  = "denied";
const CONSENT_UNSET   = "unset";

let ga4Booted     = false;
let clarityBooted = false;
let consentState  = readStoredConsent();
let pageViewsSent = 0;
let eventsSent    = 0;
const _idempotencyGuard = new Set();

// Session-lived cache of the last reported page path so we never
// double-count when React StrictMode double-invokes an effect.
let _lastReportedPath = null;

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

export function isGA4Enabled()     { return GA_ELIGIBLE; }
export function isClarityEnabled() { return CL_ELIGIBLE; }
export function isAnalyticsEnabledInEnv() { return ANALYTICS_ENABLED_ENV; }
export function getConsentState()  { return consentState; }

/**
 * One-shot bootstrap. Call once on app boot. Providers are loaded
 * ONLY when analytics is env-enabled AND the visitor has granted
 * consent — matches the "deny by default" spec §7 rule.
 */
export function initAnalytics() {
  if (typeof window === "undefined") return;
  // Always initialise Consent Mode so downstream tags respect it.
  _initConsentMode();
  // Admin exclusion: on direct entry to any /admin* URL, do NOT
  // boot GA4 or Clarity. When the visitor later navigates to a
  // public route, `trackPageView()` will lazily boot the providers.
  if (_isAdminPath(_currentPathLower())) return;
  if (consentState === CONSENT_GRANTED) _bootProvidersIfEligible();
}

/**
 * Record the visitor's consent choice.
 * @param {"granted"|"denied"} value
 */
export function setAnalyticsConsent(value) {
  const v = value === CONSENT_GRANTED ? CONSENT_GRANTED : CONSENT_DENIED;
  consentState = v;
  try { localStorage.setItem(ANALYTICS_CONSENT_KEY, v); } catch { /* private mode */ }
  _pushConsentMode(v);
  if (v === CONSENT_GRANTED) _bootProvidersIfEligible();
  // No teardown on "denied" — a page reload clears providers cleanly
  // and localStorage now blocks them from booting next time. This is
  // the pragmatic path recommended by Google Consent Mode docs.
}

/**
 * Re-open the consent question. Wipes the stored answer and returns
 * to the "unset" state so the banner shows again on next mount.
 */
export function resetAnalyticsConsent() {
  consentState = CONSENT_UNSET;
  try { localStorage.removeItem(ANALYTICS_CONSENT_KEY); } catch { /* ignore */ }
}

/**
 * Fire a `page_view` for the current SPA route. Path is sanitised
 * before transmission so magic tokens / IDs / claim codes never
 * reach GA4 or Clarity.
 */
export function trackPageView(rawPath) {
  if (typeof window === "undefined") return;
  const path = rawPath || (window.location.pathname + window.location.search);
  // Admin exclusion — case-insensitive. Bail BEFORE mutating any
  // module state so the admin route is completely invisible to GA4
  // and to the idempotency cache. Guards against BOTH the raw path
  // and the sanitised path in case a rule ever rewrites an admin
  // route into a non-admin bucket.
  if (_isAdminPath(path) || _isAdminPath(_currentPathLower())) return;
  const normalised = sanitizePath(path);
  if (_isAdminPath(normalised)) return;
  // On the first non-admin page view of the session we ensure the
  // production GA4 loader has actually run — the static
  // `public/index.html` snippet skips itself when the visitor's
  // initial URL was /admin*, and this call resumes tracking cleanly
  // when they navigate to a public route.
  if (typeof window.__cuInitGA4 === "function") {
    try { window.__cuInitGA4(); } catch { /* swallow */ }
  }
  // Idempotency for React StrictMode double-invocations.
  if (normalised === _lastReportedPath) return;
  _lastReportedPath = normalised;
  pageViewsSent += 1;

  // Note (2026-02 · GA4 Tag Coverage fix): we deliberately do NOT
  // gate `page_view` behind `consentState === CONSENT_GRANTED`.
  // Google Consent Mode v2 is already primed via
  // `gtag('consent','default',{ analytics_storage:'denied' })` in
  // `public/index.html` and `_initConsentMode()` — that tells GA4 to
  // process this hit as a cookieless/modeled event when consent is
  // denied. Blocking the call entirely made 45+ dynamic detail pages
  // (order-missing/quote/*, /certificates/*, /collectors/*, etc.)
  // register as "no GA4 tag firing" in GA4's Tag Coverage diagnostic
  // — the fix restores full coverage while still honouring the
  // visitor's actual consent choice (cookies are still gated by
  // Consent Mode inside gtag itself).
  // Provider boot may have been skipped by `initAnalytics()` when
  // the visitor landed directly on /admin — try again now that
  // we're on a public route.
  _bootProvidersIfEligible();
  if (_ga4Available() && typeof window.gtag === "function") {
    const params = {
      page_location: _sanitizeUrl(window.location.href),
      page_path:     normalised,
      page_title:    document.title,
      user_type:     _pathUserType(normalised),
      content_group: _contentGroup(normalised),
    };
    try { window.gtag("event", "page_view", params); } catch { /* swallow */ }
  }
  // Clarity has no Consent Mode equivalent — keep the consent gate.
  if (consentState !== CONSENT_GRANTED) return;
  if (CL_ELIGIBLE && typeof window.clarity === "function") {
    try { window.clarity("set", "page", normalised); } catch { /* swallow */ }
  }
}

/**
 * Fire an arbitrary event. `params` is a flat object of anonymous
 * scalars — anything looking like an identifier is dropped by the
 * PII filter inside {@link _sanitizeParams}. `options.once=true`
 * gates the event through the in-memory idempotency guard so major
 * conversions never double-fire from a double-click.
 */
export function track(eventName, params, options) {
  if (typeof window === "undefined") return;
  if (!eventName) return;
  // Admin exclusion — any event fired while the user is on an
  // /admin* route is dropped before it touches the idempotency
  // cache or counters.
  if (_isAdminPath(_currentPathLower())) return;
  const opts = options || {};
  if (opts.once) {
    const key = `${eventName}::${opts.key || _hashParams(params)}`;
    if (_idempotencyGuard.has(key)) return;
    _idempotencyGuard.add(key);
  }
  eventsSent += 1;
  // GA4 side (2026-02 fix): fire events even when consent is
  // "denied" so Google Consent Mode can collect cookieless/modeled
  // hits. `gtag('consent','default','denied')` at boot instructs GA4
  // to strip identifiers server-side — removing the gate here is
  // what turned GA4's Key Events report from "0 conversions across
  // every day" into real data. Clarity has no equivalent gating so
  // it still requires explicit consent.
  const clean = _sanitizeParams(params);
  if (_ga4Available() && typeof window.gtag === "function") {
    try { window.gtag("event", eventName, clean); } catch { /* swallow */ }
    // GA4 standard-event twin — auto-fires the ecommerce-schema
    // event GA4 needs for Key Events / Conversions reporting. Keeps
    // the existing custom names for backwards-compat.
    try { _emitGa4Twin(eventName, params, options); } catch { /* swallow */ }
  }
  if (consentState !== CONSENT_GRANTED) return;
  if (CL_ELIGIBLE && typeof window.clarity === "function") {
    try {
      window.clarity("event", eventName);
      Object.entries(clean).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        window.clarity("set", k, String(v));
      });
    } catch { /* swallow */ }
  }
}

/**
 * GA4 standard-event twin. When one of the internal custom events
 * fires, ALSO fire the equivalent GA4 standard event so Gil's Key
 * Events / Conversions reporting works out of the box (previously
 * showed 0 conversions across every day / every channel because
 * only custom names were emitted).
 *
 * Mapping — kept intentionally small so it's the only thing to
 * audit when a marketing report suddenly disagrees with the app:
 *
 *   missing_quote_generated       → generate_lead
 *   missing_order_submitted       → purchase (transaction_id from once.key)
 *   quote_converted_to_order      → purchase (transaction_id from once.key)
 *   signup_completed              → sign_up
 *   certificate_claim_completed   → certificate_claimed  (custom key event)
 *
 * `once.key` at the call sites is already the order/quote/claim
 * public identifier — reused as `transaction_id` for GA4 ecommerce
 * so the same idempotency guarantees apply. `value` + `currency`
 * come straight from the source event params.
 */
function _emitGa4Twin(name, params, options) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const p = params || {};
  const opts = options || {};
  const val = Number(p.order_value != null ? p.order_value : p.value);
  const currency = p.currency || "USD";
  const txn = _stripPII(opts.key || p.transaction_id) || undefined;
  if (name === "missing_quote_generated") {
    window.gtag("event", "generate_lead", {
      value: Number.isFinite(val) ? val : undefined,
      currency,
    });
  } else if (name === "missing_order_submitted" || name === "quote_converted_to_order") {
    window.gtag("event", "purchase", {
      transaction_id: txn,
      value: Number.isFinite(val) ? val : undefined,
      currency,
    });
  } else if (name === "signup_completed") {
    window.gtag("event", "sign_up", {
      method: p.login_method || p.method || "password",
    });
  } else if (name === "certificate_claim_completed") {
    window.gtag("event", "certificate_claimed", {
      album_year: p.album_year || undefined,
      cover_type: p.cover_type || undefined,
    });
  }
}

/**
 * Attach an anonymous user identifier (e.g. hashed collector id).
 * NEVER pass email / phone / name — those are stripped anyway but
 * the caller should not attempt it.
 */
export function identify(userId, traits) {
  if (typeof window === "undefined") return;
  if (consentState !== CONSENT_GRANTED) return;
  const cleanId = _stripPII(userId);
  const cleanTraits = _sanitizeParams(traits);
  if (GA_ELIGIBLE && typeof window.gtag === "function") {
    try {
      window.gtag("set", "user_properties", cleanTraits);
      if (cleanId) window.gtag("config", RAW_GA_ID, { user_id: cleanId });
    } catch { /* swallow */ }
  }
  if (CL_ELIGIBLE && typeof window.clarity === "function") {
    try { window.clarity("identify", cleanId || ""); } catch { /* swallow */ }
  }
}

/** Diagnostics — never used for UI decisions. */
export function analyticsStatus() {
  return {
    ga4: {
      enabled: GA_ELIGIBLE, booted: ga4Booted,
      measurementId: GA_ELIGIBLE ? RAW_GA_ID : null,
    },
    clarity: {
      enabled: CL_ELIGIBLE, booted: clarityBooted,
      projectId: CL_ELIGIBLE ? RAW_CL_ID : null,
    },
    envEnabled: ANALYTICS_ENABLED_ENV,
    consent: consentState,
    pageViewsSent, eventsSent,
  };
}

// ─────────────────────────────────────────────────────────────
// Event catalog · project spec §9. All names are lowercase snake_case
// (GA4 convention). NEVER pass customer identity into params.
// ─────────────────────────────────────────────────────────────
export const EVENT = Object.freeze({
  // GA4 standard events (added 2026-02 for Key Events / Conversions
  // reporting). Fired automatically alongside the internal names
  // below by `_emitGa4Twin` — callers can also invoke these directly
  // when they want the standard event without the custom shadow.
  GENERATE_LEAD:              "generate_lead",
  PURCHASE:                   "purchase",
  SIGN_UP:                    "sign_up",
  CERTIFICATE_CLAIMED:        "certificate_claimed",
  // Missing Stickers
  MISSING_QUOTE_STARTED:      "missing_quote_started",
  MISSING_LIST_PARSED:        "missing_list_parsed",
  MISSING_QUOTE_GENERATED:    "missing_quote_generated",
  MISSING_QUOTE_RESUMED:      "missing_quote_resumed",
  MISSING_CHECKOUT_STARTED:   "missing_checkout_started",
  DELIVERY_METHOD_SELECTED:   "delivery_method_selected",
  MISSING_ORDER_SUBMITTED:    "missing_order_submitted",
  PAYMENT_SENT_CLICKED:       "payment_sent_clicked",
  // Quotes
  QUOTE_RESUME_ATTEMPTED:     "quote_resume_attempted",
  QUOTE_RESUME_SUCCEEDED:     "quote_resume_succeeded",
  QUOTE_CONVERTED_TO_ORDER:   "quote_converted_to_order",
  // Certificates
  CERTIFICATE_ORDER_STARTED:  "certificate_order_started",
  CERTIFICATE_ORDER_SUBMITTED:"certificate_order_submitted",
  CERTIFICATE_CLAIM_STARTED:  "certificate_claim_started",
  CERTIFICATE_CLAIM_COMPLETED:"certificate_claim_completed",
  // Accounts
  SIGNUP_STARTED:             "signup_started",
  SIGNUP_COMPLETED:           "signup_completed",
  LOGIN_COMPLETED:            "login_completed",
  ALBUM_ADDED:                "album_added",
  MISSING_LIST_IMPORTED:      "missing_list_imported",
  DUPLICATE_LIST_IMPORTED:    "duplicate_list_imported",
  PROFILE_COMPLETED:          "profile_completed",
  // Fulfillment
  PICKUP_CHANGED_TO_DELIVERY: "pickup_changed_to_delivery",
  // Legacy aliases — kept so existing pages keep firing while the
  // catalog is upgraded. Do NOT remove without a GA4 report cutover.
  COPY_INVOICE_CLICKED:       "copy_invoice_clicked",
  PUBLIC_ALBUM_VIEWED:        "public_album_viewed",
  PUBLIC_PROFILE_VIEWED:      "public_profile_viewed",
  CERTIFICATE_VIEWED:         "certificate_viewed",
  ACCOUNT_CREATED:            "signup_completed",     // → spec name
  CERTIFICATE_CLAIMED:        "certificate_claim_completed",
  ALBUM_CREATED:              "album_added",
  ALBUM_DELETED:              "album_deleted",
  ALBUM_COMPLETED:            "album_completed",
  MISSING_IMPORTED:           "missing_list_imported",
  DUPLICATES_IMPORTED:        "duplicate_list_imported",
  COLLECTION_IMPORTED:        "collection_imported",
});

// ─────────────────────────────────────────────────────────────
// URL / param sanitisation — exported for the Jest suite.
// ─────────────────────────────────────────────────────────────

/**
 * Normalise a raw pathname (with or without query string) to an
 * analytics-safe label. Never sends the actual identifier.
 *
 * Examples:
 *   /orders/GM-26-ABC12?t=magictok  → /orders/order-tracker
 *   /admin/fulfillment/GM-26-ABC12  → /admin/fulfillment/order-detail
 *   /certificates/CLAIM-99XY        → /certificates/certificate-detail
 *   /shop/panini-fifa-world-cup     → /shop/panini-fifa-world-cup (unchanged)
 */
export function sanitizePath(rawPath) {
  if (!rawPath) return "/";
  const [pathOnly, queryString = ""] = String(rawPath).split("?");
  let p = pathOnly.trim() || "/";

  const rules = [
    { re: /^\/orders\/[^/]+$/,                       to: "/orders/order-tracker" },
    { re: /^\/orders\/[^/]+\/pay$/,                  to: "/orders/order-payment" },
    { re: /^\/orders\/[^/]+\/review$/,               to: "/orders/order-review" },
    { re: /^\/admin\/orders\/[^/]+$/,                to: "/admin/orders/order-detail" },
    { re: /^\/admin\/fulfillment\/[^/]+$/,           to: "/admin/fulfillment/order-detail" },
    { re: /^\/admin\/certificate-orders\/[^/]+$/,    to: "/admin/certificate-orders/order-detail" },
    { re: /^\/certificates\/[^/]+$/,                 to: "/certificates/certificate-detail" },
    { re: /^\/certificate\/[^/]+$/,                  to: "/certificate/certificate-detail" },
    { re: /^\/claim\/[^/]+$/,                        to: "/claim/certificate-claim" },
    { re: /^\/collectors\/[^/]+$/,                   to: "/collectors/public-profile" },
    { re: /^\/c\/[^/]+$/,                            to: "/c/public-profile" },
    { re: /^\/albums\/[^/]+$/,                       to: "/albums/public-album" },
    { re: /^\/my-collection\/albums\/[^/]+$/,        to: "/my-collection/albums/album-detail" },
    { re: /^\/quote\/[^/]+$/,                        to: "/quote/quote-detail" },
  ];
  for (const r of rules) {
    if (r.re.test(p)) { p = r.to; break; }
  }

  const q = _filterQuery(queryString);
  return q ? `${p}?${q}` : p;
}

function _filterQuery(qs) {
  if (!qs) return "";
  const KEEP = new Set([
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gclid", "fbclid", "page", "tab",
  ]);
  const kept = [];
  for (const pair of qs.split("&")) {
    const [k] = pair.split("=");
    if (KEEP.has((k || "").toLowerCase())) kept.push(pair);
  }
  return kept.join("&");
}

function _sanitizeUrl(fullUrl) {
  try {
    const u = new URL(fullUrl);
    const sanitisedPath = sanitizePath(u.pathname + u.search);
    return `${u.protocol}//${u.host}${sanitisedPath}`;
  } catch {
    return sanitizePath(fullUrl);
  }
}

/**
 * PII-safe parameter sanitiser. Rejects any key that looks like a
 * private identifier and truncates long strings.
 */
function _sanitizeParams(obj) {
  if (!obj || typeof obj !== "object") return {};
  const DROP_KEYS = new Set([
    "email", "phone", "name", "first_name", "last_name",
    "address", "shipping_address", "line1", "line2",
    "postal_code", "zip", "city", "state", "country",
    "magic_token", "zelle_reference", "claim_code",
    "order_id", "public_id", "quote_id", "certificate_number",
    "collector_id", "public_collector_id",
    "password", "otp", "token",
  ]);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (DROP_KEYS.has(String(k).toLowerCase())) continue;
    if (typeof v === "object") {
      out[k] = JSON.stringify(v).slice(0, 480);
    } else {
      const s = typeof v === "string" ? v : String(v);
      out[k] = s.length > 480 ? s.slice(0, 480) : (typeof v === "number" ? v : s);
    }
  }
  return out;
}

function _stripPII(id) {
  if (!id) return null;
  const s = String(id);
  // Never send anything that looks like an email or phone.
  if (s.includes("@")) return null;
  if (/^\+?\d[\d\s\-().]{6,}$/.test(s)) return null;
  return s.slice(0, 96);
}

function _pathUserType(path) {
  return (path || "").startsWith("/admin") ? "admin" : "customer";
}

function _contentGroup(path) {
  if (!path) return "other";
  if (path.startsWith("/admin"))              return "admin";
  if (path.startsWith("/order-missing"))      return "missing_stickers";
  if (path.startsWith("/orders"))             return "order_tracker";
  if (path.startsWith("/certificates") || path.startsWith("/certificate") || path.startsWith("/claim"))
                                              return "certificates";
  if (path.startsWith("/my-collection") || path.startsWith("/collectors") || path.startsWith("/c/"))
                                              return "collector";
  if (path.startsWith("/shop"))               return "shop";
  if (path === "/")                            return "home";
  return "other";
}

function _hashParams(p) {
  try { return JSON.stringify(p || {}).slice(0, 64); }
  catch { return String(Math.random()).slice(2, 8); }
}

// ─────────────────────────────────────────────────────────────
// Consent Mode integration
// ─────────────────────────────────────────────────────────────

function readStoredConsent() {
  try {
    const v = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (v === CONSENT_GRANTED || v === CONSENT_DENIED) return v;
  } catch { /* private mode / SSR */ }
  return CONSENT_UNSET;
}

function _initConsentMode() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const denied = consentState !== CONSENT_GRANTED;
  try {
    window.gtag("consent", "default", {
      analytics_storage: denied ? "denied" : "granted",
      ad_storage: "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });
  } catch { /* swallow */ }
}

function _pushConsentMode(state) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  try {
    window.gtag("consent", "update", {
      analytics_storage: state === CONSENT_GRANTED ? "granted" : "denied",
    });
  } catch { /* swallow */ }
}

// ─────────────────────────────────────────────────────────────
// Provider bootstrap
// ─────────────────────────────────────────────────────────────

function _bootProvidersIfEligible() {
  if (GA_ELIGIBLE && !ga4Booted) _bootGA4();
  if (CL_ELIGIBLE && !clarityBooted) _bootClarityWhenIdle();
}

function _bootGA4() {
  if (ga4Booted) return;
  ga4Booted = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  // Dedup guard: the production `public/index.html` snippet already
  // loads `gtag.js` for `G-6WV8XH1N9D` and calls `gtag('config', ...)`
  // with `send_page_view: false`. When that snippet is present we do
  // NOT re-configure or re-inject the script — otherwise we'd have two
  // config calls on the same measurement ID and a duplicate network
  // request. `trackPageView()` in this file still fires manual
  // page_view events on route changes; the pre-installed gtag handles
  // them via the shared `window.gtag` queue.
  var preinstalled = typeof document !== "undefined" &&
    !!document.querySelector('script[data-analytics-source="index-html"]');
  if (preinstalled) return;
  window.gtag("js", new Date());
  window.gtag("config", RAW_GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(RAW_GA_ID)}`;
  s.setAttribute("data-analytics-provider", "ga4");
  document.head.appendChild(s);
}

function _bootClarity() {
  if (clarityBooted || window.clarity) { clarityBooted = true; _clarityMask(); return; }
  clarityBooted = true;
  window.clarity = window.clarity || function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.clarity.ms/tag/${encodeURIComponent(RAW_CL_ID)}`;
  s.setAttribute("data-analytics-provider", "clarity");
  s.addEventListener("load", _clarityMask, { once: true });
  document.head.appendChild(s);
  // Queue masking immediately so the moment the script loads it
  // honours the mask config.
  _clarityMask();
}

function _clarityMask() {
  if (typeof window.clarity !== "function") return;
  try {
    // Mask every text-carrying input plus anything we explicitly
    // tag with `data-clarity-mask` (login / register / payment
    // forms already have masking attributes applied at markup).
    window.clarity("mask", "input", "text");
    window.clarity("mask", "input[type=email]");
    window.clarity("mask", "input[type=tel]");
    window.clarity("mask", "input[type=password]");
    window.clarity("mask", "textarea");
    window.clarity("mask", "[data-clarity-mask]");
    // Block sensitive admin surfaces from being recorded at all.
    window.clarity("set", "customer_recording", "policy_v1");
  } catch { /* swallow — Clarity may not have loaded yet */ }
}

function _bootClarityWhenIdle() {
  const start = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(_bootClarity, { timeout: 4000 });
    } else {
      setTimeout(_bootClarity, 1500);
    }
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}
